import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  Build as BuildIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  VerifiedUser as VerifiedUserIcon,
  Speed as SpeedIcon,
  Psychology as PsychologyIcon,
  DataObject as DataObjectIcon,
  Biotech as BiotechIcon,
  CloudDone as CloudDoneIcon,
  Shield as ShieldIcon,
  Analytics as AnalyticsIcon,
  Sync as SyncIcon,
  LocalOffer as LocalOfferIcon,
  Support as SupportIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import Nav1 from '../components/Nav1';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../utils/supabaseClient';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  backgroundColor: '#fff',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: 150,
    height: 150,
    background: 'radial-gradient(circle, rgba(255,227,2,0.1) 0%, rgba(255,255,255,0) 70%)',
    zIndex: 0,
  }
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 72,
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&.Mui-selected': {
    color: '#1a1a1a',
    fontWeight: 700,
    '& .MuiSvgIcon-root': {
      color: '#FFE302',
    }
  },
  '& .MuiSvgIcon-root': {
    transition: 'color 0.3s ease',
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: '#FFE302',
    height: 3,
  },
  borderBottom: '1px solid #eee',
  marginBottom: theme.spacing(4),
}));

const UploadButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  color: '#FFE302',
  fontWeight: 600,
  padding: '10px 24px',
  borderRadius: 8,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#000',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  }
}));

const ScanButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFE302',
  color: '#1a1a1a',
  fontWeight: 700,
  padding: '10px 24px',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(255,227,2,0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#f5d800',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(255,227,2,0.4)',
  },
  '&.Mui-disabled': {
    backgroundColor: '#f5f5f5',
    color: '#999'
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  transition: 'all 0.3s ease',
  border: '1px solid #eee',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
    borderColor: '#FFE302',
  }
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#FFE302',
  backgroundColor: '#1a1a1a',
  borderRadius: '50%',
  width: 48,
  height: 48,
  marginRight: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    fontSize: 24,
  }
}));

const ResultCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  border: '1px solid #eee',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    borderColor: '#FFE302',
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 800,
  marginBottom: 24,
  position: 'relative',
  display: 'inline-block',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 3,
    backgroundColor: '#FFE302',
    borderRadius: 2
  }
}));

const StyledProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#FFE302',
  },
  backgroundColor: 'rgba(0,0,0,0.05)',
}));

const FileScan = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setFile(null);
    setScanResults(null);
    setError(null);
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setScanResults(null);
    }
  };

  const handleScan = async () => {
    if (!file) {
      setError('Please select a file to scan');
      return;
    }

    try {
      setScanning(true);
      setError(null);

      // Simulate scanning process
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simulate different results based on scan type
      const results = {
        0: { // Image Analysis
          faultDetected: true,
          damageType: 'Screen Damage',
          confidence: '89%',
          recommendations: ['Replace LCD panel', 'Check display connector'],
          estimatedCost: '$150-200'
        },
        1: { // Diagnostic Report
          issues: ['Memory failure', 'Battery degradation'],
          severity: 'Medium',
          suggestedActions: ['Memory module replacement', 'Battery replacement'],
          technicalDetails: 'DDR4 RAM, 2400MHz, 8GB required'
        },
        2: { // Warranty
          valid: true,
          expiryDate: '2024-12-31',
          coverage: 'Full parts and labor',
          manufacturer: 'Example Corp'
        },
        3: { // Parts
          partIdentified: 'Samsung LCD A51',
          compatibility: ['Model A51', 'Model A515F'],
          alternatives: ['Original', 'OEM Compatible'],
          marketplaceLinks: ['#', '#']
        }
      }[activeTab];

      setScanResults(results);

    } catch (err) {
      console.error('Scan error:', err);
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const scanTypes = [
    { icon: <ImageIcon />, label: 'Image Analysis', description: 'Scan device images for visible damage' },
    { icon: <DescriptionIcon />, label: 'Diagnostic Report', description: 'Analyze diagnostic reports' },
    { icon: <ReceiptIcon />, label: 'Warranty Check', description: 'Verify warranty status' },
    { icon: <BuildIcon />, label: 'Parts Finder', description: 'Identify replacement parts' },
  ];

  const renderResults = () => {
    if (!scanResults) return null;

    return (
      <Box sx={{ mt: 4 }}>
        <SectionTitle variant="h5" gutterBottom>
          Scan Results
        </SectionTitle>
        <Grid container spacing={3}>
          {Object.entries(scanResults).map(([key, value]) => (
            <Grid item xs={12} md={6} key={key}>
              <ResultCard>
                <Box sx={{ 
                  p: 2, 
                  borderBottom: '1px solid #eee',
                  backgroundColor: 'rgba(255, 227, 2, 0.05)',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#1a1a1a'
                    }}
                  >
                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                  </Typography>
                </Box>
                <CardContent>
                  {Array.isArray(value) ? (
                    <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                      {value.map((item, i) => (
                        <Box 
                          component="li" 
                          key={i}
                          sx={{ 
                            mb: 1,
                            '&:last-child': { mb: 0 }
                          }}
                        >
                          <Typography variant="body2">{item}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography 
                      variant="body1"
                      sx={{
                        fontWeight: key === 'confidence' || key === 'valid' ? 700 : 400,
                        color: key === 'valid' && value === true ? '#4caf50' : 'inherit'
                      }}
                    >
                      {value.toString()}
                    </Typography>
                  )}
                </CardContent>
              </ResultCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderFeatures = () => {
    const features = [
      {
        icon: <SpeedIcon />,
        title: "Fast Processing",
        description: "Advanced algorithms process your files in seconds"
      },
      {
        icon: <PsychologyIcon />,
        title: "AI-Powered Analysis",
        description: "Machine learning models for accurate fault detection"
      },
      {
        icon: <DataObjectIcon />,
        title: "Smart Diagnostics",
        description: "Detailed technical analysis and repair recommendations"
      },
      {
        icon: <BiotechIcon />,
        title: "Precision Detection",
        description: "High-accuracy fault identification and classification"
      },
      {
        icon: <CloudDoneIcon />,
        title: "Secure Storage",
        description: "Encrypted cloud storage for all your scan reports"
      },
      {
        icon: <ShieldIcon />,
        title: "Fraud Protection",
        description: "Advanced counterfeit detection systems"
      },
      {
        icon: <AnalyticsIcon />,
        title: "Detailed Reports",
        description: "Comprehensive analysis with visual insights"
      },
      {
        icon: <SyncIcon />,
        title: "Real-time Updates",
        description: "Live progress tracking and instant results"
      },
      {
        icon: <LocalOfferIcon />,
        title: "Parts Marketplace",
        description: "Direct access to verified replacement parts"
      },
      {
        icon: <SupportIcon />,
        title: "Expert Support",
        description: "24/7 technical assistance and guidance"
      }
    ];

    return (
      <Box sx={{ mt: 8, mb: 4 }}>
        <Box sx={{ 
          textAlign: 'center', 
          mb: 5,
          position: 'relative'
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800,
              mb: 2,
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 80,
                height: 3,
                backgroundColor: '#FFE302',
                borderRadius: 2
              }
            }}
          >
            Advanced Features & Capabilities
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              maxWidth: 700, 
              mx: 'auto',
              mt: 3
            }}
          >
            Our cutting-edge technology provides comprehensive device diagnostics with industry-leading accuracy
          </Typography>
        </Box>
        
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <FeatureCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mb: 2,
                  }}>
                    <FeatureIcon>
                      {feature.icon}
                    </FeatureIcon>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ 
          mt: 6, 
          textAlign: 'center',
          p: 4,
          borderRadius: 4,
          backgroundColor: '#1a1a1a',
          color: '#fff'
        }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#FFE302' }}>
            Trusted by Industry Leaders
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.8)' }}>
            Our AI-powered scanning technology is used by leading manufacturers and repair centers worldwide
          </Typography>
          <Button 
            variant="contained" 
            sx={{ 
              backgroundColor: '#FFE302',
              color: '#1a1a1a',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: '#f5d800',
              }
            }}
            endIcon={<ArrowForwardIcon />}
            onClick={() => window.open('/about', '_blank')}
          >
            Learn More About Our Technology
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#f9f9f9'
    }}>
      <Nav1 />
      
      <Container maxWidth="lg" sx={{ flex: 1, py: 6 }}>
        <Box sx={{ 
          textAlign: 'center', 
          mb: 5,
          position: 'relative'
        }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 900, 
              mb: 2,
              color: '#1a1a1a',
              textTransform: 'uppercase'
            }}
          >
            AI-Powered Device Scanner
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#666', 
              maxWidth: 700, 
              mx: 'auto',
              mb: 1
            }}
          >
            Upload files for instant AI analysis and diagnostics
          </Typography>
        </Box>
        
        <StyledPaper>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {scanTypes.map((type, index) => (
              <StyledTab
                key={index}
                icon={type.icon}
                label={type.label}
                wrapped
              />
            ))}
          </StyledTabs>

          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 1, 
                fontWeight: 700,
                color: '#1a1a1a'
              }}
            >
              {scanTypes[activeTab].label}
            </Typography>
            <Typography 
              color="text.secondary" 
              paragraph
              sx={{ 
                borderLeft: '3px solid #FFE302',
                pl: 2,
                py: 0.5
              }}
            >
              {scanTypes[activeTab].description}
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: '#d32f2f'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ 
            textAlign: 'center', 
            mb: 4,
            p: 4,
            borderRadius: 3,
            backgroundColor: 'rgba(0,0,0,0.02)',
            border: '1px dashed #ddd'
          }}>
            <input
              accept={activeTab === 0 ? "image/*" : "*/*"}
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="file-upload">
              <UploadButton
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
              >
                Select File
              </UploadButton>
            </label>
            
            {file && (
              <Box sx={{ mt: 3 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(255, 227, 2, 0.1)',
                    borderRadius: 2,
                    display: 'inline-block',
                    fontWeight: 500
                  }}
                >
                  Selected: {file.name}
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <ScanButton
                    onClick={handleScan}
                    disabled={scanning}
                    startIcon={scanning ? null : <AnalyticsIcon />}
                  >
                    {scanning ? 'Scanning...' : 'Start Scan'}
                  </ScanButton>
                </Box>
              </Box>
            )}
          </Box>

          {scanning && (
            <Box sx={{ mb: 4 }}>
              <StyledProgress />
              <Typography 
                sx={{ 
                  mt: 2, 
                  textAlign: 'center',
                  fontWeight: 500,
                  color: '#1a1a1a'
                }}
              >
                Analyzing your file...
              </Typography>
            </Box>
          )}

          {renderResults()}

          {renderFeatures()}

          <Box sx={{ 
            mt: 6, 
            p: 3, 
            bgcolor: '#1a1a1a', 
            borderRadius: 3,
            color: '#fff'
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 2,
                fontWeight: 600,
                color: '#FFE302'
              }}
            >
              Premium Features:
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <Tooltip title="Secure Cloud Storage">
                  <Box sx={{ 
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      transform: 'translateY(-3px)'
                    }
                  }}>
                    <StorageIcon sx={{ fontSize: 32, color: '#FFE302', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Secure Storage
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
              <Grid item xs={6} md={3}>
                <Tooltip title="Fraud Detection">
                  <Box sx={{ 
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      transform: 'translateY(-3px)'
                    }
                  }}>
                    <SecurityIcon sx={{ fontSize: 32, color: '#FFE302', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Fraud Detection
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
              <Grid item xs={6} md={3}>
                <Tooltip title="AI Analysis">
                  <Box sx={{ 
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      transform: 'translateY(-3px)'
                    }
                  }}>
                    <VerifiedUserIcon sx={{ fontSize: 32, color: '#FFE302', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      AI Analysis
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
              <Grid item xs={6} md={3}>
                <Tooltip title="24/7 Support">
                  <Box sx={{ 
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      transform: 'translateY(-3px)'
                    }
                  }}>
                    <SupportIcon sx={{ fontSize: 32, color: '#FFE302', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      24/7 Support
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        </StyledPaper>
      </Container>

      <Footer />
    </Box>
  );
};

export default FileScan; 